// engine-wording.ts — the progress page's words, PURE (session 7 §7e: tested under `npm test`). The engine's user state
// decides them (contracts userStateOf, served by the backend); the ledger's actual outcome is said only when it is true.
// (B107) the page's own words are read through w() (lib/words.ts): editable in Admin → Words.
import { w } from "@/lib/words.ts";
export type Situation = "working" | "needs_you" | "waiting_on_us" | "done" | "stopped";
export type UserStateView = { situation: Situation; message: string; actions: string[]; basis?: string };
/** The video's credits as the ledger has them (the backend's creditOutcomeOf). */
export type CreditOutcome = { state: "none" | "reserved" | "charged" | "released" | "refunded"; amount: number };

/** The ledger in words: a reservation RELEASED was never spent; a charge REFUNDED came back; nothing is promised otherwise. */
export const creditsLine = (c: CreditOutcome | null): string | null =>
  !c || c.amount <= 0 ? null
  : c.state === "released" ? w("page.progress.no_credits_used")
  : c.state === "refunded" ? w(c.amount === 1 ? "page.progress.credits_back.one" : "page.progress.credits_back.many", { amount: c.amount }) : null;

/** Whether the run has stopped / is still working — the engine's situation first, the bare status only for an older engine. */
export function progressFlags(userState: UserStateView | null, runStatus: string | null): { failed: boolean; working: boolean } {
  const failed = userState ? userState.situation === "stopped" : runStatus === "failed" || runStatus === "cancelled";
  return { failed, working: userState ? userState.situation === "working" : !failed };
}

/** (creative-1 C1.5, review J1) Whether the finished video is shown — to watch, download (which accepts it) and post.
 *  Not while the final screening HOLDS it (the run summary's basis), and not while the product still projects it as
 *  `rendering` (the screening hasn't passed yet): the progress page says "We're checking your finished video" instead. */
export function finishedVideoShown(status: string | null | undefined, userState: { basis?: string } | null): boolean {
  if (userState?.basis === "failure:screening_held" || userState?.basis === SCREENING_RETRYING) return false;   // (zip 43) its file is being replaced
  if (userState?.basis === "rehearsal_rendered") return false;   // (B119; C2.22) stand-in clips: never watched, downloaded (accepted) or posted
  return status !== "rendering";
}

/** (creative zip 43, planning 10-17 12:30) the final screening reads a line again: the video is working — never attention
 *  or a failure — and the customer is told so in these words. */
export const SCREENING_RETRYING = "failure:screening_retrying";

/** The headline: the engine's own words whenever it is not simply working; a line being re-recorded is said. */
export function headlineFor(userState: UserStateView | null, runStatus: string | null): string {
  const { failed } = progressFlags(userState, runStatus);
  if (userState?.basis === SCREENING_RETRYING) return w("page.progress.re_recording");
  return userState && userState.situation !== "working" ? userState.message : failed ? w("page.progress.could_not_be_made") : w("page.progress.making");
}
